/**
 * Every failure the Law Library API can return, as one shape the page can show (#1964).
 *
 * The routes answer in several shapes (app/api/law_library.py): a coded detail
 * {code, retry_after, message} from `_failed` and filing; the seat refusal
 * {code: "seat", seat}; the courts refusal {code: "scope", reason, message};
 * plain string details (role, page switched off, matter access); FastAPI's
 * validation list. From the browser's side there is also no response at all,
 * and http.ts's circuit-breaker cancellation. This is the only place those
 * shapes are read.
 */
import * as Sentry from '@sentry/react';
import axios from 'axios';

export type SeatState = 'ready' | 'pending' | 'connect' | 'oauth_only' | 'unreadable';
export type UnreadySeat = Exclude<SeatState, 'ready'>;

export type LawLibraryErrorKind =
  | 'unavailable'
  | 'network'
  | 'courts'
  | 'choose_courts'
  | 'seat'
  | 'limit'
  | 'not_allowed'
  | 'not_found'
  | 'restart'
  | 'ambiguous'
  | 'rejected'
  | 'filing'
  | 'invalid'
  | 'page_off'
  | 'forbidden';

export const UNAVAILABLE_MESSAGE = 'Legal research is unavailable right now.';
export const COURTS_MESSAGE = 'Maverick could not search these courts.';
/** A conversation (or a first question) without courts: Ask's own refusal, not Maverick's (spec 4.3). */
export const CHOOSE_COURTS_MESSAGE = 'Choose the courts for this conversation before asking.';

export const SEAT_MESSAGES: Record<UnreadySeat, string> = {
  connect: 'Connect Maverick to search case law and statutes.',
  oauth_only: 'Search needs a Maverick seat key; a sign-in connection is not enough.',
  pending: 'Your Maverick seat is not ready yet. Try again.',
  unreadable: 'Valentine could not read your Maverick connection. Try again.',
};

const UNREADY = new Set<string>(['pending', 'connect', 'oauth_only', 'unreadable']);

// The server's code -> what the page does with it, and its words when the server sends none.
const CODED: Record<string, { kind: LawLibraryErrorKind; fallback: string }> = {
  credential: { kind: 'seat', fallback: 'Maverick did not accept your seat. Reconnect Maverick in My Account.' },
  quota: { kind: 'limit', fallback: 'This Maverick subscription has no requests left.' },
  rate_limited: { kind: 'limit', fallback: "You have reached Maverick's request limit." },
  not_allowed: { kind: 'not_allowed', fallback: 'Maverick does not accept requests from this network.' },
  unavailable: { kind: 'unavailable', fallback: UNAVAILABLE_MESSAGE },
  courts_rejected: { kind: 'courts', fallback: COURTS_MESSAGE },
  scope: { kind: 'courts', fallback: COURTS_MESSAGE },
  courts: { kind: 'choose_courts', fallback: CHOOSE_COURTS_MESSAGE },
  run_active: { kind: 'rejected', fallback: 'Wait for the current answer to finish before changing courts.' },
  courts_changed: { kind: 'rejected', fallback: "This conversation's courts were just changed. Send your question again." },
  not_found: { kind: 'not_found', fallback: 'Maverick does not have this.' },
  invalid_continuation: { kind: 'restart', fallback: 'This reading position is no longer valid. Open the text again.' },
  ambiguous: { kind: 'ambiguous', fallback: 'That citation matches more than one authority.' },
  rejected: { kind: 'rejected', fallback: 'Maverick could not run that request.' },
  no_folder: { kind: 'filing', fallback: 'This matter has no Dropbox folder, so nothing was saved.' },
  opinion_mismatch: { kind: 'filing', fallback: 'The opinion on screen is not the one Maverick would file. Nothing was saved.' },
  already_filed: { kind: 'filing', fallback: "This opinion is already in the matter's Research folder." },
  source_unavailable: { kind: 'filing', fallback: 'The opinion could not be fetched. Nothing was saved.' },
  upload_failed: { kind: 'filing', fallback: "Dropbox did not confirm the file. Check the matter's Research folder before saving again." },
};

let nextErrorId = 1;

export class LawLibraryError extends Error {
  /** Increases with every error made: a notice keyed by it restarts its countdown for a new failure. */
  readonly id: number;
  readonly kind: LawLibraryErrorKind;
  readonly code: string | null;
  readonly retryAfter: number | null;
  /**
   * When Retry may be pressed (epoch ms), fixed when the answer arrived, which is when
   * this error is made. A notice derives its countdown from it and the clock, so a
   * remount keeps the same deadline (second pass R10).
   */
  readonly retryAt: number | null;
  readonly status: number | null;
  readonly seat: UnreadySeat | null;

  constructor(init: {
    kind: LawLibraryErrorKind;
    message: string;
    code?: string | null;
    retryAfter?: number | null;
    status?: number | null;
    seat?: UnreadySeat | null;
  }) {
    super(init.message);
    this.name = 'LawLibraryError';
    this.id = nextErrorId++;
    this.kind = init.kind;
    this.code = init.code ?? null;
    this.retryAfter = init.retryAfter ?? null;
    this.retryAt = this.retryAfter ? Date.now() + this.retryAfter * 1000 : null;
    this.status = init.status ?? null;
    this.seat = init.seat ?? null;
  }
}

function seconds(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value.trim());
  return null;
}

function sentence(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function validationMessage(detail: unknown[]): string {
  const first = detail.find((item) => item && typeof item === 'object') as { msg?: unknown } | undefined;
  const msg = sentence(first?.msg);
  // Only `msg`: FastAPI's `input` field echoes what was sent, which can be a question.
  return msg ? `That request was not accepted: ${msg.replace(/^Value error, /, '')}` : 'That request was not accepted.';
}

export function headerOf(headers: unknown, name: string): string | null {
  if (!headers || typeof headers !== 'object') return null;
  const value = (headers as Record<string, unknown>)[name.toLowerCase()];
  if (typeof value === 'string') return value;
  return typeof value === 'number' ? String(value) : null;
}

export function lawLibraryErrorFromResponse(status: number, data: unknown, retryAfterHeader?: string | null): LawLibraryError {
  const detail = data && typeof data === 'object' ? (data as { detail?: unknown }).detail : undefined;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const coded = detail as Record<string, unknown>;
    const code = typeof coded.code === 'string' ? coded.code : null;
    if (code === 'seat') {
      const seat: UnreadySeat = typeof coded.seat === 'string' && UNREADY.has(coded.seat)
        ? (coded.seat as UnreadySeat)
        : 'unreadable';
      return new LawLibraryError({ kind: 'seat', code, status, seat, message: SEAT_MESSAGES[seat] });
    }
    const retryAfter = seconds(coded.retry_after) ?? seconds(retryAfterHeader);
    const known = code ? CODED[code] : undefined;
    if (known) {
      return new LawLibraryError({
        kind: known.kind,
        code,
        status,
        retryAfter,
        seat: code === 'credential' ? 'connect' : null,
        message: sentence(coded.message) ?? known.fallback,
      });
    }
    return new LawLibraryError({ kind: 'unavailable', code, status, retryAfter, message: UNAVAILABLE_MESSAGE });
  }
  if (Array.isArray(detail)) {
    return new LawLibraryError({ kind: 'invalid', status, message: validationMessage(detail) });
  }
  const text = sentence(detail);
  if (status === 404 && text === 'Not found') {
    return new LawLibraryError({ kind: 'page_off', status, message: 'The new Law Library is not switched on for you.' });
  }
  if (text && status === 404) return new LawLibraryError({ kind: 'not_found', status, message: text });
  if (text && status === 403) return new LawLibraryError({ kind: 'forbidden', status, message: text });
  if (text && status >= 400 && status < 500) return new LawLibraryError({ kind: 'rejected', status, message: text });
  return new LawLibraryError({ kind: 'unavailable', status, retryAfter: seconds(retryAfterHeader), message: UNAVAILABLE_MESSAGE });
}

export function toLawLibraryError(error: unknown): LawLibraryError {
  if (error instanceof LawLibraryError) return error;
  if (axios.isCancel(error)) {
    const message = sentence((error as { message?: unknown }).message);
    return new LawLibraryError({ kind: 'network', message: message ?? 'The request was cancelled. Try again.' });
  }
  if (axios.isAxiosError(error)) {
    if (error.response) {
      return lawLibraryErrorFromResponse(error.response.status, error.response.data, headerOf(error.response.headers, 'retry-after'));
    }
    return new LawLibraryError({ kind: 'network', message: 'Valentine could not be reached. Check your connection and try again.' });
  }
  // Not an HTTP failure (e.g. a TypeError in an interceptor): report its type only, never its message.
  Sentry.captureMessage(`law-library: unexpected error ${(error as Error)?.name}`, {
    level: 'warning',
    tags: { source: 'law_library_error_module' },
  });
  return new LawLibraryError({ kind: 'unavailable', message: UNAVAILABLE_MESSAGE });
}
